import { z } from 'zod'
import { LATIN_AND_NUMBERS_ONLY } from '@/utils/constants/regex'

const voucherSchema = z
  .string()
  .regex(LATIN_AND_NUMBERS_ONLY, { message: 'forms.global.error.latinAndNumber' })
  .max(20, { message: 'forms.global.error.max.length.20' })
  .or(z.literal(''))

interface IVoucher {
  inputValue: string
  error: string
}

export const useVoucherStore = defineStore('voucher', () => {
  const VOUCHER: IVoucher = { inputValue: '', error: '' }
  const MAX_VOUCHERS = 5

  const vouchers = ref([{ ...VOUCHER }])
  const forceOpen = ref(false)

  const validateVoucher = (voucher: IVoucher) => {
    if (hasDuplicates(vouchers.value, voucher)) {
      setDuplicateEntryErrorMessage(voucher)

      return
    }

    const result = voucherSchema.safeParse(voucher.inputValue)
    if (!result.success) {
      voucher.error = result.error.errors[0].message

      return
    }

    voucher.error = ''
  }

  const checkAllVouchersForDuplicates = (vouchers: IVoucher[], initialCheck: boolean = false) => {
    if (vouchers.length === 1) {
      if (initialCheck) {
        return
      }

      vouchers[0].error = ''

      return
    }

    vouchers.forEach((voucher) => {
      if (hasDuplicates(vouchers, voucher)) {
        setDuplicateEntryErrorMessage(voucher)
      }
    })
  }

  const hasDuplicates = (vouchers: IVoucher[], voucher: IVoucher) => {
    return vouchers.some((v) => v !== voucher && v.inputValue.trim() === voucher.inputValue.trim())
  }

  const setDuplicateEntryErrorMessage = (voucher: IVoucher) => {
    voucher.error = 'vouchers.error.duplicateEntries'
  }

  const canAddVoucher = computed(() => vouchers.value.length < MAX_VOUCHERS)

  const addVoucherField = () => {
    if (!canAddVoucher.value) return

    vouchers.value.push({ ...VOUCHER })
  }

  const removeVoucherField = (index: number) => {
    if (vouchers.value.length <= 1) {
      vouchers.value[0].inputValue = ''
      vouchers.value[0].error = ''

      return
    }

    vouchers.value.splice(index, 1)
    checkAllVouchersForDuplicates(vouchers.value)
  }

  const removeEmptyVouchers = () => {
    vouchers.value = vouchers.value.filter((voucher) => !!voucher.inputValue)

    if (!vouchers.value.length) {
      vouchers.value.push({ ...VOUCHER })
    }
  }

  const hasVoucherErrors = computed(() => vouchers.value.some((voucher) => !!voucher.error))
  const vouchersCount = computed(() => vouchers?.value?.filter((voucher) => !!voucher.inputValue).length)

  return {
    vouchers,
    forceOpen,
    canAddVoucher,
    vouchersCount,
    hasVoucherErrors,
    addVoucherField,
    removeVoucherField,
    removeEmptyVouchers,
    validateVoucher,
    checkAllVouchersForDuplicates
  }
})
